import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Box,
  ThemeProvider,
  createTheme,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import api from "../../../services/api";
import logo from "../../../assets/imgs/logo-dark.svg";
import { useNavigate } from "react-router-dom";

// Supabase theme
const supabaseTheme = createTheme({
  palette: {
    primary: {
      main: "hsl(210, 60%, 53%)",
      contrastText: "#ffffff",
    },
  },
  typography: {
    fontFamily:
      "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
});

const MOUSignatureModal = ({ open, onClose, onSubmit }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [mouData, setMouData] = useState(null);
  const [exhibitAContent, setExhibitAContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [mouId, setMouId] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({ ip: "", browser: "" });
  const totalPages = 7;
  const [verificationTimestamp, setVerificationTimestamp] = useState("");
  const navigate = useNavigate();

  // Fetch MOU data and Exhibit A content
  useEffect(() => {
    const fetchMouData = async () => {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem("accessToken");

        if (!accessToken) {
          console.error("No access token found");
          return;
        }

        // First, get the pending MOU ID
        const pendingResponse = await api.get(`/accounts/my-pending-mou`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const fetchedMouId = pendingResponse.data.mou_id;
        setMouId(fetchedMouId);

        // Then fetch the full MOU details
        if (fetchedMouId) {
          const mouResponse = await api.get(
            `/accounts/view-for-signing/${fetchedMouId}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          setMouData(mouResponse.data);

          // Fetch Exhibit A content
          const exhibitAResponse = await api.get(
            `/accounts/spoc/get-exhibit-a/${fetchedMouId}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          // Replace template variables with actual values
          let htmlContent = exhibitAResponse.data.exhibit_a_content;
          const instituteName =
            mouResponse.data?.institute?.institute_name || "Institute Name";

          // Replace all occurrences of {{ institute_name }}
          htmlContent = htmlContent.replace(
            /\{\{\s*institute_name\s*\}\}/g,
            instituteName
          );

          setExhibitAContent(htmlContent);
        }
      } catch (error) {
        console.error("Error fetching MOU data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchMouData();
    }
  }, [open]);

  // Get device info
  const getDeviceInfo = async () => {
    try {
      // Get browser info
      const userAgent = navigator.userAgent;
      let browserName = "Unknown Browser";

      if (userAgent.indexOf("Firefox") > -1) {
        browserName = "Mozilla Firefox";
      } else if (userAgent.indexOf("Chrome") > -1) {
        browserName = "Google Chrome";
      } else if (userAgent.indexOf("Safari") > -1) {
        browserName = "Safari";
      } else if (userAgent.indexOf("Edge") > -1) {
        browserName = "Microsoft Edge";
      } else if (
        userAgent.indexOf("Opera") > -1 ||
        userAgent.indexOf("OPR") > -1
      ) {
        browserName = "Opera";
      }

      // Get IP address (using a public API)
      let ipAddress = "Unable to fetch";
      try {
        const ipResponse = await fetch("https://api.ipify.org?format=json");
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch (error) {
        console.error("Error fetching IP:", error);
      }

      return { ip: ipAddress, browser: browserName };
    } catch (error) {
      console.error("Error getting device info:", error);
      return { ip: "Unable to fetch", browser: "Unknown Browser" };
    }
  };

  const instituteName =
    mouData?.institute?.institute_name || "Invertis University";
  const instituteAddress = mouData?.institute?.address || "";
  const membershipName = mouData?.membership?.membership_name || "";
  const validityYears = mouData?.membership?.validity_years || 0;
  const validDays = mouData?.membership?.valid_days || 0;
  const validFrom = mouData?.membership?.valid_from || "";
  const validTill = mouData?.membership?.valid_till || "";
  const features = mouData?.features || [];

  // Helper: convert valid_days into a human-readable duration string
  // e.g. 730 → "twenty-four (24) months" or "two (2) years"
  const numberWords = [
    "", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
    "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
    "seventeen", "eighteen", "nineteen", "twenty", "twenty-one", "twenty-two",
    "twenty-three", "twenty-four", "twenty-five", "twenty-six", "twenty-seven",
    "twenty-eight", "twenty-nine", "thirty", "thirty-one", "thirty-two",
    "thirty-three", "thirty-four", "thirty-five", "thirty-six"
  ];
  const toWord = (n) => (n > 0 && n < numberWords.length ? numberWords[n] : String(n));

  const getDurationText = (days) => {
    if (!days) return "";
    const totalMonths = Math.round(days / 30.4375);
    const years = Math.floor(totalMonths / 12);
    const remMonths = totalMonths % 12;
    if (remMonths === 0 && years > 0) {
      return `${toWord(years)} (${years}) year${years > 1 ? "s" : ""}`;
    }
    return `${toWord(totalMonths)} (${totalMonths}) month${totalMonths !== 1 ? "s" : ""}`;
  };

  const durationText = getDurationText(validDays);
  const logoUrl = mouData?.logo_url || "";
  const signerName = mouData?.signer_info?.name || "";
  const signerDesignation = mouData?.signer_info?.designation || "";
  const dateOfSigning = mouData?.membership?.invoice_date || "";

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 2MB)
      const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSizeInBytes) {
        alert("File size too large. Please upload an image smaller than 2MB.");
        e.target.value = ""; // Reset file input
        return;
      }

      // Validate file type
      const validTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        alert("Invalid file type. Please upload PNG or JPEG image only.");
        e.target.value = "";
        return;
      }

      // Validate image dimensions
      const img = new Image();
      const previewUrl = URL.createObjectURL(file);

      img.onload = () => {
        // Maximum dimensions
        const maxWidth = 800;
        const maxHeight = 400;

        if (img.width > maxWidth || img.height > maxHeight) {
          alert(
            `Image dimensions too large. Maximum allowed: ${maxWidth}x${maxHeight}px.\nYour image: ${img.width}x${img.height}px`
          );
          URL.revokeObjectURL(previewUrl);
          e.target.value = "";
          return;
        }

        // Minimum dimensions
        const minWidth = 200;
        const minHeight = 50;

        if (img.width < minWidth || img.height < minHeight) {
          alert(
            `Image dimensions too small. Minimum required: ${minWidth}x${minHeight}px.\nYour image: ${img.width}x${img.height}px`
          );
          URL.revokeObjectURL(previewUrl);
          e.target.value = "";
          return;
        }

        // All validations passed
        setSignatureFile(file);
        setSignaturePreview(previewUrl);
        setShowOtpInput(false);
        setTermsAccepted(false);
        setVerificationSuccess(false);
      };

      img.onerror = () => {
        alert("Failed to load image. Please try another file.");
        URL.revokeObjectURL(previewUrl);
        e.target.value = "";
      };

      img.src = previewUrl;
    }
  };

  // Step 1: Upload Signature and Send OTP
  const handleValidateSignature = async () => {
    if (!signatureFile || !mouId || !termsAccepted) return;

    try {
      setUploadingSignature(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        alert("No access token found. Please login again.");
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("mou_id", mouId);
      formData.append("signature_file", signatureFile);

      // Call upload signature API
      const response = await api.post("/accounts/upload-signature", formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        alert(response.data.message || "OTP sent successfully to your email!");
        setShowOtpInput(true);
      }
    } catch (error) {
      console.error("Error uploading signature:", error);
      alert(
        error.response?.data?.detail ||
          "Failed to upload signature. Please try again."
      );
    } finally {
      setUploadingSignature(false);
    }
  };

  // Step 2: Verify OTP
  const handleOtpValidation = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6 || !mouId) return;

    try {
      setVerifyingOtp(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        alert("No access token found. Please login again.");
        return;
      }

      const formData = new FormData();
      formData.append("mou_id", mouId);
      formData.append("otp_code", otpValue);

      const response = await api.post(
        "/accounts/verify-signature-otp",
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success && response.data.signature_verified) {
        // Get device info
        const info = await getDeviceInfo();
        setDeviceInfo(info);

        // Capture current date/time in Indian locale format
        const now = new Date();
        const formattedDateTime = now.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
        setVerificationTimestamp(formattedDateTime);

        setVerificationSuccess(true);
        setShowOtpInput(false);
      }
      alert(response.data.message || "OTP verified successfully!");
      navigate("/sub-details");
    } catch (error) {
      console.error("Error verifying OTP:", error);
      alert(
        error.response?.data?.detail ||
          "Failed to verify OTP. Please try again."
      );
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleClose = () => {
    if (verificationSuccess && signatureFile) {
      onSubmit(signatureFile);
    }

    setSignatureFile(null);
    setSignaturePreview(null);
    setShowOtpInput(false);
    setOtp(["", "", "", "", "", ""]);
    setCurrentPage(1);
    setTermsAccepted(false);
    setVerificationSuccess(false);
    setDeviceInfo({ ip: "", browser: "" });
    setVerificationTimestamp("");
    onClose();
  };

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (signaturePreview) {
        URL.revokeObjectURL(signaturePreview);
      }
    };
  }, [signaturePreview]);

  return (
    <ThemeProvider theme={supabaseTheme}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            height: "90vh",
            width: "90%",
            margin: "auto",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header with close button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "8px 12px",
            borderBottom: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <DialogContent
          sx={{
            padding: "16px 24px",
            overflow: "auto",
            flexGrow: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "500px",
              }}
            >
              <div className="text-gray-500">Loading MOU...</div>
            </Box>
          ) : (
            <Box
              sx={{
                bgcolor: "white",
                p: { xs: 2, sm: 3, md: 4 },
                minHeight: "500px",
                fontSize: { xs: "10px", sm: "11px", md: "12px" },
              }}
            >
              {currentPage === 1 && (
                <Page1 instituteName={instituteName} logoUrl={logoUrl} />
              )}
              {currentPage === 2 && <Page2 durationText={durationText} />}
              {currentPage === 3 && <Page3 />}
              {currentPage === 4 && <Page4 durationText={durationText} />}
              {currentPage === 5 && (
                <Page5
                  signatureFile={signatureFile}
                  signaturePreview={signaturePreview}
                  onFileChange={handleFileChange}
                  onValidateSignature={handleValidateSignature}
                  showOtpInput={showOtpInput}
                  otp={otp}
                  setOtp={setOtp}
                  onOtpValidation={handleOtpValidation}
                  instituteName={instituteName}
                  instituteAddress={instituteAddress}
                  signerName={signerName}
                  signerDesignation={signerDesignation}
                  termsAccepted={termsAccepted}
                  setTermsAccepted={setTermsAccepted}
                  uploadingSignature={uploadingSignature}
                  verifyingOtp={verifyingOtp}
                  verificationSuccess={verificationSuccess}
                  deviceInfo={deviceInfo}
                  verificationTimestamp={verificationTimestamp}
                  dateOfSigning={dateOfSigning}
                />
              )}
              {currentPage === 6 && <Page6 exhibitAContent={exhibitAContent} />}
              {currentPage === 7 && (
                <Page7 membershipName={membershipName} features={features} />
              )}
            </Box>
          )}
        </DialogContent>

        {/* Navigation */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 24px",
            borderTop: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          <Button
            onClick={prevPage}
            disabled={currentPage === 1}
            startIcon={<ChevronLeft size={18} />}
            sx={{
              fontSize: "12px",
              textTransform: "none",
              color: currentPage === 1 ? "#999" : "primary.main",
            }}
          >
            Previous
          </Button>

          <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#666" }}>
            Page {currentPage} of {totalPages}
          </Box>

          <Button
            onClick={nextPage}
            disabled={currentPage === totalPages}
            endIcon={<ChevronRight size={18} />}
            sx={{
              fontSize: "12px",
              textTransform: "none",
              color: currentPage === totalPages ? "#999" : "primary.main",
            }}
          >
            Next
          </Button>
        </Box>
      </Dialog>
    </ThemeProvider>
  );
};

const Page1 = ({ instituteName, logoUrl }) => (
  <div className="space-y-8 text-center">
    <h1 className="text-2xl font-bold underline decoration-2 underline-offset-4">
      MEMORANDUM OF UNDERSTANDING
    </h1>
    <div className="text-xl font-bold">BETWEEN</div>
    <div className="flex justify-center my-6">
      <div className="w-[200px] flex items-center justify-center">
        <img
          src={logo}
          alt={`EduSkills Foundation Logo`}
          className="max-w-full max-h-full object-contain"
        />
      </div>
    </div>
    <div className="text-xl font-bold">EduSkills Foundation ("EduSkills")</div>
    <div className="text-xl font-bold my-6">AND</div>
    <div className="flex justify-center my-6">
      {logoUrl ? (
        <div className="w-[150px] flex items-center justify-center">
          <img
            src={logoUrl}
            alt={`${instituteName} Logo`}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      ) : (
        <div className="w-72 h-24 bg-gray-200 flex items-center justify-center border border-gray-300">
          <span className="text-sm text-gray-600">[{instituteName} Logo]</span>
        </div>
      )}
    </div>
    <div className="text-xl font-bold">{instituteName}</div>
    <div className="text-xl font-bold mt-12">
      FOR EDUSKILLS INDUSTRY CENTER OF EXCELLENCE PROGRAM
    </div>
    <div className="mt-16 text-justify leading-relaxed">
      <p className="mb-4">
        This Memorandum of Understanding ("MOU") is effective from the date of
        signatures below ("Effective Date");
      </p>
      <p className="mb-4">
        hereinafter, each a "Party" or together, "the Parties".{" "}
        <strong>EduSkills</strong> will be referred as 'First party' and{" "}
        <strong>{instituteName}</strong> will be referred as 'Second party'.
      </p>
      <p className="mb-4">
        The Parties have been discussing a proposed collaboration in the area of{" "}
        <strong>
          "Skill Development, IT Education and Workforce Development Programs"
        </strong>{" "}
        ("Collaboration Areas").
      </p>
      <p>Now the Parties agree as follows:</p>
    </div>
  </div>
);

const Page2 = ({ durationText }) => (
  <div className="space-y-6 text-justify leading-relaxed">
    <div>
      <h2 className="text-lg font-bold underline decoration-2 underline-offset-4 mb-4">
        A. NON-BINDING TERMS.
      </h2>
      <p className="mb-4">
        The Parties understand that MOU is intended only to summarize the
        current understanding of the parties with respect to the principal terms
        of the proposed collaboration.
      </p>
      <p>
        The Parties understand that this <strong>MOU</strong> is intended only
        to summarize the current understanding of the Parties with respect to
        the Collaboration Areas and to facilitate their further discussions
        regarding certain objectives of the proposed partnership, as more fully
        described in Exhibit A, which are non-binding on the Parties. The terms
        and objectives set forth in Exhibit A shall be used for discussion
        purposes only and shall not be deemed to create any rights or
        obligations for or on behalf of any Party.
      </p>
    </div>

    <div>
      <h2 className="text-lg font-bold underline decoration-2 underline-offset-4 mb-4">
        B. BINDING TERMS.
      </h2>
      <p className="mb-4">
        The Parties hereby acknowledge and agree that the following subsections
        under this Section B (Binding Terms) shall be binding and create legal
        rights and obligations on the Parties.
      </p>

      <div>
        <h3 className="text-base font-bold mb-3">
          1. Confidential Information
        </h3>
        <p className="mb-4">
          In this MOU "Confidential Information" means any information or data,
          irrespective of the form and nature thereof, that is material to the
          Party disclosing such information ("Discloser") to the other Party
          ("Recipient"), including, but not limited to technical know-how,
          specifications, designs, models, software, techniques, drawings,
          processes, as well as industrial and intellectual property rights,
          business information, trade secrets, customer lists, financial
          information, process, costs, sales and marketing plans, information
          posted on their respective websites or any other information with the
          Recipient knows or reasonably ought to know is confidential,
          proprietary or trade secret information of the Discloser.
        </p>
        <p>
          The Recipient shall at all times, both during the term of this MOU and
          for a period of at least{" "}
          <strong>{durationText || "three (3) years"}</strong> after its expiration or
          termination, keep in trust and confidence all such Confidential
          Information, and shall not use such Confidential Information other
          than for the purpose of fulfilling its obligations under this MOU, nor
          shall the Recipient disclose any such Confidential Information to any
          third party without the Disclosing Party's prior written consent. Upon
          termination of this MOU or written request from the Discloser, the
          Recipient shall, within fifteen (15) days from such termination or
          request, return all Confidential Information and any and all copies
          thereof to the Discloser, or at the Discloser's option, destroy the
          Confidential Information and any and all copies thereof, and certify
          such destruction.
        </p>
      </div>
    </div>
  </div>
);

const Page3 = () => (
  <div className="space-y-6 text-justify leading-relaxed">
    <p className="mb-4">
      The obligations of confidentiality set forth herein shall not apply to
      information which:
    </p>
    <ul className="list-none space-y-3 ml-6 mb-6">
      <li className="flex gap-3">
        <span className="font-semibold">a.</span>
        <span>
          Was rightfully in possession of or known to the Recipient without any
          obligation of confidentiality prior to receiving it from the
          Discloser;
        </span>
      </li>
      <li className="flex gap-3">
        <span className="font-semibold">b.</span>
        <span>
          Is, or subsequently becomes, legally and publicly available without
          breach of this MOU;
        </span>
      </li>
      <li className="flex gap-3">
        <span className="font-semibold">c.</span>
        <span>
          Is rightfully obtained by the Recipient from a source other than the
          Discloser, without any obligation of confidentiality; or
        </span>
      </li>
      <li className="flex gap-3">
        <span className="font-semibold">d.</span>
        <span>
          Is developed by or for the Recipient without use of the Confidential
          Information.
        </span>
      </li>
    </ul>
    <p className="mb-4">
      The Recipient may only disclose Confidential Information pursuant to a
      valid order issued by a court or governmental agency, provided that the
      Recipient provides the Discloser:
    </p>
    <ul className="list-none space-y-3 ml-6 mb-6">
      <li className="flex gap-3">
        <span className="font-semibold">a.</span>
        <span>Prior written notice of such obligation; and</span>
      </li>
      <li className="flex gap-3">
        <span className="font-semibold">b.</span>
        <span>
          The opportunity to oppose such disclosure or obtain a protective
          order.
        </span>
      </li>
    </ul>
    <p className="mb-4">
      The Confidential Information shall remain the property of the Discloser
      and no license to any existing or future intellectual property rights or
      Confidential Information is granted or implied under this MOU.
    </p>
    <p className="mb-6">
      All Confidential Information is provided "AS IS". The Discloser shall not
      be liable for any inaccuracy or completeness of the Confidential
      Information, nor are there any express or implied representations or
      warranties by either party to the other, including with respect to the
      infringement of any intellectual property rights or any right of privacy,
      or any rights of third persons.
    </p>
    <div>
      <h3 className="text-base font-bold mb-3">2. Scope</h3>
      <p>
        The scope of this MOU does not cover any development work, testing,
        licensing, distribution, support, sales, or maintenance. Should the
        Parties decide to pursue such matter that is outside the scope of this
        MOU, the Parties shall enter into a development, license, distribution,
        or other appropriate written agreement(s) accordingly.
      </p>
    </div>
  </div>
);

const Page4 = ({ durationText }) => (
  <div className="space-y-6 text-justify leading-relaxed">
    <div>
      <h3 className="text-base font-bold mb-3">3. Publicity</h3>
      <p>
        The Parties hereby agree that no press release or other public
        announcements regarding this MOU or any agreements contemplated thereby
        shall be made without prior review and written agreement signed by a
        duly authorized representative of the other Party.
      </p>
    </div>
    <div>
      <h3 className="text-base font-bold mb-3">4. Term and Termination</h3>
      <p>
        The Parties agree that this MOU shall be effective as of the Effective
        Date and shall continue in effect until the earliest occurrence of one
        of the following: (1) the execution of a definitive MOU or definitive
        agreement relating to the scope of this MOU; (2) written notice by one
        Party to the other of termination of this MOU; or{" "}
        <strong>{durationText || "thirty-six (36) months"}</strong> from the Effective Date of this
        MOU. Section B (including all subsections) shall survive any termination
        of this MOU. Upon expiry of the Term, it is the intention of the Parties
        to further progress the solutions initiated under this MOU towards
        overall achievement of the objectives and outcomes contemplated herein.
        Second party shall, to this extent, endeavor to continue maintaining and
        promoting such solutions and projects to further the objectives set
        forth in the MOU.
      </p>
    </div>
    <div>
      <h3 className="text-base font-bold mb-3">5. Limitation of Liability</h3>
      <p className="mb-4">
        Except for liability relating to either Party's breach of the other
        Party's confidential information or intellectual property rights, in no
        event shall either Party be liable to the other Party for any direct,
        indirect, incidental, special, punitive or consequential damages or loss
        of profits, lost revenue or loss of data.
      </p>
      <p>
        Notwithstanding the foregoing, nothing in this MOU shall limit the
        Parties liability for bodily injury or death caused by its negligence or
        its liability in the tort of deceit.
      </p>
    </div>
    <div>
      <h3 className="text-base font-bold mb-3">
        6. Intellectual property rights
      </h3>
      <p>
        Each Party will retain the exclusive rights, title and ownership of all
        its pre-existing Intellectual Property, Confidential Information and
        materials (including, without limitation, proprietary ideas, sketches,
        diagrams, text, know-how, concepts, proofs of concepts, artwork,
        software, algorithms, methods, processes, identifier codes or other
        technology) owned or developed by such Party.
      </p>
    </div>
    <div>
      <h3 className="text-base font-bold mb-3">
        7. Governing laws and jurisdiction
      </h3>
      <p>
        The Parties shall make efforts in good faith to amicably resolve all
        disputes as may arise out of or in connection with this MOU. This MOU
        shall be subject to and governed by the laws of India and the Indian
        courts shall have exclusive jurisdiction over any claim arising under
        this <strong>MOU</strong>. Notwithstanding the foregoing, either Party
        may seek interim injunctive relief in any court of appropriate
        jurisdiction with respect to any alleged breach of such Party's
        intellectual property or proprietary rights.
      </p>
    </div>
  </div>
);

// OTP Input Component
const OtpInput = ({ otp, setOtp, onComplete }) => {
  const inputRefs = useRef([]);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last character
    setOtp(newOtp);

    // Auto focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if all fields are filled
    if (newOtp.every((digit) => digit !== "") && index === 5) {
      onComplete && onComplete();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle arrow keys
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split("").forEach((char, index) => {
      if (index < 6) newOtp[index] = char;
    });
    setOtp(newOtp);

    // Focus the last filled input or the next empty one
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          className="w-10 h-10 text-center text-lg font-bold border-2 border-gray-300 rounded focus:border-blue-500 focus:outline-none"
        />
      ))}
    </div>
  );
};

const Page5 = ({
  signatureFile,
  signaturePreview,
  onFileChange,
  onValidateSignature,
  showOtpInput,
  otp,
  setOtp,
  onOtpValidation,
  instituteName,
  instituteAddress,
  signerName,
  signerDesignation,
  dateOfSigning,
  termsAccepted,
  setTermsAccepted,
  uploadingSignature,
  verifyingOtp,
  verificationSuccess,
  deviceInfo,
  verificationTimestamp,
}) => {
  return (
    <div className="space-y-6 text-justify leading-relaxed">
      <div>
        <h3 className="text-base font-bold mb-3">8. Costs</h3>
        <p>
          Unless otherwise agreed upon in writing, each Party shall bear its own
          costs and expenses arising in connection with this MOU and the
          possible negotiations, completion and implementation of definitive
          Agreements if any, including but not limited to costs of experts,
          consultants, lawyers and travel expenses.
        </p>
      </div>

      <div>
        <h3 className="text-base font-bold mb-3">9. Amendment</h3>
        <p>
          This MOU may be amended at any time by the mutual written assent of
          the Parties.
        </p>
      </div>

      <div>
        <h3 className="text-base font-bold mb-3">10. Severability</h3>
        <p className="mb-6">
          Should any portion of this MOU be determined to be illegal or
          unenforceable, the remainder of the MOU shall continue in full force
          and effect, and either party may renegotiate the terms affected by the
          severance.
        </p>
      </div>

      <p className="mb-8">
        IN WITNESS WHEREOF, the parties hereto have executed this{" "}
        <strong>MOU</strong> by persons duly authorized as of the date and year
        first above written.
      </p>

      {/* Signature Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-400 text-sm">
          <thead>
            <tr>
              <th className="border border-gray-400 p-3 text-left bg-gray-50 font-bold">
                Signed for and on behalf of{" "}
                <span className="underline">EduSkills Foundation</span>
              </th>
              <th className="border border-gray-400 p-3 text-left bg-gray-50 font-bold">
                Signed for and on behalf of{" "}
                <span className="underline">{instituteName}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                className="border border-gray-400 p-4"
                // style={{ filter: "blur(4px)" }}
              >
                <div className="mb-1">By:</div>
                <div className="font-semibold text-lg">Signed</div>
                <div className="text-gray-500 italic">Authorized Signatory</div>
              </td>
              <td className="border border-gray-400 p-4">
                <div className="mb-2">By:</div>

                {/* Verification Success Message */}
                {verificationSuccess && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-3">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircleIcon
                        sx={{ color: "#22c55e", fontSize: "28px" }}
                      />
                      <span className="text-green-700 font-bold text-base">
                        Signature Verified Successfully!
                      </span>
                    </div>

                    {/* Signature Preview */}
                    <div className="border border-green-300 rounded p-2 bg-white mb-3">
                      <img
                        src={signaturePreview}
                        alt="Verified Signature"
                        className="max-w-[180px] max-h-[70px] object-contain"
                      />
                    </div>

                    {/* Device Information */}
                    <div className="space-y-2 text-xs">
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[70px]">
                          Device IP:
                        </span>
                        <span className="text-gray-600">{deviceInfo.ip}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[70px]">
                          Browser:
                        </span>
                        <span className="text-gray-600">
                          {deviceInfo.browser}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[70px]">
                          Date & Time:
                        </span>
                        <span className="text-gray-600">
                          {verificationTimestamp}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[70px]">
                          Status:
                        </span>
                        <span className="text-green-600 font-semibold">
                          ✓ Verified
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Initial Upload Button */}
                {!signatureFile && !verificationSuccess && (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    <label
                      htmlFor="signature-upload"
                      className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 transition text-xs"
                    >
                      <CloudUploadIcon sx={{ fontSize: "16px" }} />
                      Upload Signature
                    </label>
                    <input
                      id="signature-upload"
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={onFileChange}
                    />
                  </div>
                )}

                {/* Signature Preview with Terms & Validate Button */}
                {signatureFile &&
                  signaturePreview &&
                  !showOtpInput &&
                  !verificationSuccess && (
                    <div className="flex flex-col gap-3">
                      {/* Signature Preview */}
                      <div className="border-2 border-dashed border-gray-300 rounded p-2 bg-gray-50">
                        <img
                          src={signaturePreview}
                          alt="Signature Preview"
                          className="max-w-[200px] max-h-[80px] object-contain"
                        />
                      </div>

                      {/* Terms and Conditions Checkbox */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            size="small"
                            sx={{
                              padding: "4px",
                              "& .MuiSvgIcon-root": { fontSize: 18 },
                            }}
                          />
                        }
                        label={
                          <span className="text-xs text-gray-700">
                            I accept the terms and conditions of this MOU
                          </span>
                        }
                        sx={{ margin: 0 }}
                      />

                      {/* Validate Button */}
                      <Button
                        onClick={onValidateSignature}
                        disabled={!termsAccepted || uploadingSignature}
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: "hsl(210, 60%, 53%)",
                          color: "#ffffff",
                          fontSize: "11px",
                          padding: "6px 20px",
                          textTransform: "none",
                          alignSelf: "flex-start",
                          "&:hover": {
                            backgroundColor: "hsl(210, 60%, 43%)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#e0e0e0",
                            color: "#999",
                          },
                        }}
                      >
                        {uploadingSignature
                          ? "Uploading..."
                          : "Validate Signature"}
                      </Button>

                      {/* Change File Option */}
                      <label
                        htmlFor="signature-upload-change"
                        className="text-xs text-blue-600 hover:underline cursor-pointer"
                      >
                        Change File
                      </label>
                      <input
                        id="signature-upload-change"
                        type="file"
                        className="hidden"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={onFileChange}
                      />
                    </div>
                  )}

                {/* OTP Input Section */}
                {showOtpInput && !verificationSuccess && (
                  <div className="flex flex-col gap-3 mt-3">
                    {/* Signature Preview (smaller) */}
                    <div className="border border-gray-300 rounded p-2 bg-gray-50">
                      <img
                        src={signaturePreview}
                        alt="Signature Preview"
                        className="max-w-[150px] max-h-[60px] object-contain"
                      />
                    </div>

                    {/* OTP Input */}
                    <div className="flex flex-col gap-2">
                      <p className="text-xs font-semibold text-gray-700">
                        Enter 6-digit OTP sent to your email:
                      </p>
                      <OtpInput
                        otp={otp}
                        setOtp={setOtp}
                        onComplete={onOtpValidation}
                      />
                    </div>

                    {/* Validate OTP Button */}
                    <Button
                      onClick={onOtpValidation}
                      disabled={
                        otp.some((digit) => digit === "") || verifyingOtp
                      }
                      variant="contained"
                      size="small"
                      sx={{
                        backgroundColor: "hsl(210, 60%, 53%)",
                        color: "#ffffff",
                        fontSize: "11px",
                        padding: "6px 20px",
                        textTransform: "none",
                        alignSelf: "flex-start",
                        "&:hover": {
                          backgroundColor: "hsl(210, 60%, 43%)",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e0e0e0",
                          color: "#999",
                        },
                      }}
                    >
                      {verifyingOtp ? "Verifying..." : "Validate OTP"}
                    </Button>
                  </div>
                )}

                {!verificationSuccess && (
                  <div className="text-gray-500 italic text-xs mt-2">
                    Authorized Signatory
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td className="border border-gray-400 p-4">
                <div className="font-semibold mb-1">Dr. Shubhajit Jagadev</div>
                <div className="text-gray-500 italic">Name</div>
              </td>
              <td className="border border-gray-400 p-4">
                <div className="font-semibold mb-1">
                  {signerName || "_______________"}
                </div>
                <div className="text-gray-500 italic">Name</div>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-400 p-4">
                <div className="mb-1">Chief Executive Officer</div>
                <div className="text-gray-500 italic">Designation</div>
              </td>
              <td className="border border-gray-400 p-4">
                <div className="mb-1">
                  {signerDesignation || "_______________"}
                </div>
                <div className="text-gray-500 italic">Designation</div>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-400 p-4">
                <div className="mb-1">{dateOfSigning}</div>
                <div className="text-gray-500 italic">Date</div>
              </td>
              <td className="border border-gray-400 p-4">
                <div className="mb-1">{dateOfSigning}</div>
                <div className="text-gray-500 italic">Date</div>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-400 p-4">
                <div className="font-bold mb-2">Address for communication:</div>
                <div className="text-sm">
                  #806, DLF Cyber City, Technology Corridor, Infocity,
                  Bhubaneswar – 751024, Odisha, India.
                </div>
                <div className="text-sm mt-2">
                  Email: info@eduskillsfoundation.org
                </div>
              </td>
              <td className="border border-gray-400 p-4">
                <div className="font-bold mb-2">Address for communication:</div>
                <div className="text-sm">
                  {instituteAddress || "_______________"}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// PAGE 6 - EXHIBIT A (Dynamic from API with proper styling)
const Page6 = ({ exhibitAContent }) => (
  <div
    className="exhibit-a-content"
    style={{
      textAlign: "justify",
      lineHeight: "1.75",
    }}
  >
    <style>
      {`
        .exhibit-a-content h2 {
          font-size: 18px !important;
          font-weight: 700 !important;
          margin-bottom: 16px !important;
          margin-top: 8px !important;
          color: #1a1a1a !important;
        }
        
        .exhibit-a-content h3 {
          font-size: 15px !important;
          font-weight: 700 !important;
          margin-bottom: 12px !important;
          margin-top: 12px !important;
          color: #1a1a1a !important;
        }
        
        .exhibit-a-content h4 {
          font-size: 13px !important;
          font-weight: 700 !important;
          margin-bottom: 8px !important;
          margin-top: 8px !important;
          color: #1a1a1a !important;
        }
        
        .exhibit-a-content p {
          font-size: 12px !important;
          margin-bottom: 12px !important;
          line-height: 1.75 !important;
          color: #333 !important;
        }
        
        .exhibit-a-content strong {
          font-weight: 700 !important;
          color: #000 !important;
        }
        
        .exhibit-a-content u {
          text-decoration: underline !important;
        }
        
        .exhibit-a-content ul {
          margin: 8px 0 16px 0 !important;
          padding-left: 30px !important;
          font-size: 12px !important;
          list-style-type: disc !important;
          list-style-position: outside !important;
        }
        
        .exhibit-a-content ul li {
          margin-bottom: 6px !important;
          text-align: justify !important;
          line-height: 1.6 !important;
          color: #333 !important;
          display: list-item !important;
        }
        
        /* Quill editor indented list items */
        .exhibit-a-content ul li.ql-indent-1 {
          margin-left: 30px !important;
          list-style-type: circle !important;
        }
        
        .exhibit-a-content ul li.ql-indent-2 {
          margin-left: 60px !important;
          list-style-type: square !important;
        }
        
        .exhibit-a-content ul li.ql-indent-3 {
          margin-left: 90px !important;
          list-style-type: disc !important;
        }
        
        .exhibit-a-content ul ul {
          margin: 4px 0 8px 0 !important;
          padding-left: 30px !important;
          list-style-type: circle !important;
        }
        
        .exhibit-a-content ul ul li {
          margin-bottom: 4px !important;
        }
        
        .exhibit-a-content div {
          margin-bottom: 8px !important;
        }
      `}
    </style>
    <div dangerouslySetInnerHTML={{ __html: exhibitAContent }} />
  </div>
);

// PAGE 7 - EXHIBIT B (MEMBERSHIP FEATURES)
const Page7 = ({ membershipName, features }) => {
  // Create a map of symbol to value_text for legends
  const symbolValueMap = {};
  features.forEach((feature) => {
    if (feature.symbol && feature.value_text) {
      symbolValueMap[feature.symbol] = feature.value_text;
    }
  });

  // Get unique symbols from features
  const usedSymbols = [
    ...new Set(features.map((f) => f.symbol).filter(Boolean)),
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-center mb-6">EXHIBIT B</h2>

      {/* Features Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-2 border-blue-600">
          <thead>
            <tr className="bg-blue-600 text-white">
              <th className="border border-blue-600 p-3 text-center font-bold text-sm">
                Programs / Activities
              </th>
              <th className="border border-blue-600 p-3 text-center font-bold text-sm">
                {membershipName || "Premier+ Membership"}
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map((feature, index) => (
              <tr
                key={index}
                className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}
              >
                <td className="border border-gray-300 p-2 text-xs">
                  {feature.feature_name}
                </td>
                <td className="border border-gray-300 p-2 text-xs text-center font-medium">
                  {feature.symbol || feature.value_text}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend - Show symbols with their value_text */}
      <div className="text-xs text-gray-600 mt-4">
        <div className="flex gap-6">
          {/* LEFT SIDE – Symbols with aligned colons */}
          <div className="flex flex-col space-y-1 max-w-[45%]">
            <div className="flex">
              <span className="w-6 text-center font-semibold">✓</span>
              <span className="w-3 text-center">:</span>
              <span>Eligible / Applicable</span>
            </div>

            <div className="flex">
              <span className="w-6 text-center font-semibold">TBA</span>
              <span className="w-3 text-center">:</span>
              <span>To Be Announced Soon</span>
            </div>

            <div className="flex">
              <span className="w-6 text-center font-semibold">*</span>
              <span className="w-3 text-center">:</span>
              <span>Chargeable Services</span>
            </div>

            <div className="flex">
              <span className="w-6 text-center font-semibold">✗</span>
              <span className="w-3 text-center">:</span>
              <span>Not Applicable</span>
            </div>
          </div>

          {/* RIGHT SIDE – T&C */}
          <div className="flex flex-col space-y-1 max-w-[55%]">
            <p className="italic">*T&C Applicable</p>
            <p className="italic">
              *The Membership Renewal Fees are Subject to Change without Prior
              Notice
            </p>
            <p className="italic">
              *The institute shall bear travel and hospitality expenses for
              experts attending FDPs, Tech Camps, Job Fairs, and other physical
              events.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MOUSignatureModal;
